import { Job, CommandJob, TriggerJob, JobStates } from "~/components/build/Show/lib/types";

// Essentially the same as JobStates except with the finished state resolved to either: passed, hard_failed, and soft_failed
// to allow for sorting by state.
export type ResolvedJobState =
  | Exclude<JobStates, "finished">
  | "hard_failed"
  | "soft_failed"
  | "passed";

export const JobStateOrder: ResolvedJobState[] = [
  // Failed
  "hard_failed",
  "canceled",
  "canceling",
  "timed_out",
  "expired",

  // Running
  "running",

  // Scheduled
  "accepted",
  "assigned",
  "scheduled",
  "platform_limited",
  "platform_limiting",
  "limited",
  "limiting",

  // Waiting
  "waiting",
  "blocked",

  // Soft failed
  "soft_failed",

  // Passed
  "passed",
  "unblocked",

  // Initial states (pending, skipped, broken)
  "pending",
  "skipped",
  "broken",
];

/**
 * Sort jobs by state order, then by job.sortKey
 * @param a
 * @param b
 * @returns sorted jobs
 */
// eslint-disable-next-line id-length
export const sortJobsByState = (direction: "asc" | "desc") => (a: Job, b: Job) => {
  const aState = resolveJobState(a as CommandJob | TriggerJob);
  const bState = resolveJobState(b as CommandJob | TriggerJob);
  const aIndex = JobStateOrder.indexOf(aState);
  const bIndex = JobStateOrder.indexOf(bState);

  if (aIndex !== bIndex) {
    return direction === "asc" ? aIndex - bIndex : bIndex - aIndex;
  }

  const sortKeyComparison = a.sortKey.localeCompare(b.sortKey);
  return direction === "asc" ? sortKeyComparison : -sortKeyComparison;
};

export function resolveJobState(job: CommandJob | TriggerJob): ResolvedJobState {
  if (job.state === "finished") {
    if (!job.passed) {
      return "hard_failed";
    }
    if (job.softFailed) {
      return "soft_failed";
    }
    return "passed";
  }
  // A running job that will fail is sorted alongside hard-failed jobs. Once the
  // job is terminal, the actual state and exit status are authoritative.
  if (job.type === "script" && job.willFail) {
    return "hard_failed";
  }
  return job.state;
}
